<template>
  <div>
    <h1 class="text-xl font-medium leading-6 text-gray-900 mr-2">Let's connect your calendar</h1>
    <p class="mt-1 text-sm text-gray-500">
      Connecting your calendar allows us to easily schedule phone screens, interviews, and pipeline reviews directly
      into your calendar. You can always invite your team members to join and assign them to these events instead.
    </p>

    <ul class="mt-6 grid grid-cols-1 gap-4 max-w-sm mx-auto">
      <li class="col-span-1">
        <ConnectGoogleCalendarButton />
      </li>

      <li class="col-span-1">
        <div class="border border-gray-200 rounded-full">
          <div class="-mt-px flex">
            <div class="w-0 flex-1 flex">
              <form
                :action="`/users/auth/microsoft?prompt=consent&login_hint=${currentUserStore.currentUserAccountDetails.username}`"
                method="post"
                class="cursor-pointer relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm leading-5 text-gray-700 font-medium border border-transparent rounded-full hover:text-gray-500 focus:outline-none focus:shadow-outline-indigo focus:border-indigo-300 focus:z-10 transition ease-in-out duration-150"
              >
                <MicrosoftIcon />
                <input type="hidden" name="authenticity_token" :value="csrf" />
                <button type="submit">Connect Microsoft Calendar</button>
              </form>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <div class="mt-6 py-3 flex justify-center">
      <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <TButton color="amber" label="Get Started" :disabled="true" @click="getStarted" />
      </span>
    </div>

    <Sheet>
      <div class="mt-2 flex justify-center">
        <SheetTrigger as-child>
          <button
            type="button"
            class="text-sm text-gray-500 underline cursor-pointer hover:text-gray-700 transition-colors"
          >
            Set up later
          </button>
        </SheetTrigger>
      </div>

      <SheetContent>
        <SheetHeader padded>
          <SheetTitle size="lg">Skip Calendar Setup?</SheetTitle>
        </SheetHeader>

        <Separator />

        <SheetBody padded class="py-6 space-y-5">
          <p class="text-sm text-gray-600 leading-relaxed">
            Your calendar helps power several key features. Skipping now means these won't be available until you
            connect a calendar:
          </p>

          <Alert variant="info">
            <AlertDescription>
              <ul class="text-sm space-y-2 ml-1">
                <li class="flex items-start gap-2">
                  <span class="mt-1.5 size-1 rounded-full bg-blue-400 shrink-0" />
                  Scheduling candidates for phone screens and interviews
                </li>
                <li class="flex items-start gap-2">
                  <span class="mt-1.5 size-1 rounded-full bg-blue-400 shrink-0" />
                  Assigning teammates as phone screen, interview, or pipeline review owners
                </li>
                <li class="flex items-start gap-2">
                  <span class="mt-1.5 size-1 rounded-full bg-blue-400 shrink-0" />
                  Generating self-scheduling links for candidates
                </li>
              </ul>
            </AlertDescription>
          </Alert>

          <p class="text-sm text-gray-500 leading-relaxed">
            You can connect your calendar anytime from <span class="font-medium text-gray-700">Settings</span>.
          </p>
        </SheetBody>

        <SheetFooter bordered class="flex-row justify-end gap-3">
          <SheetClose as-child>
            <Button variant="outline">Go back</Button>
          </SheetClose>
          <Button
            class="bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500"
            @click="handleSkipConfirm"
          >
            Continue without calendar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  </div>
</template>

<script>
import ConnectGoogleCalendarButton from '@/components/oauth/ConnectGoogleCalendarButton.vue';
import MicrosoftIcon from '@/components/oauth/MicrosoftIcon.vue';
import {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
} from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { useUserCalendarStore } from '@/stores/userCalendar.ts';

export default {
  components: {
    ConnectGoogleCalendarButton,
    MicrosoftIcon,
    Sheet,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetBody,
    SheetFooter,
    Alert,
    AlertDescription,
    Button,
    Separator,
  },
  setup() {
    const currentUserStore = useCurrentUserStore();
    const userCalendarStore = useUserCalendarStore();
    return { currentUserStore, userCalendarStore };
  },
  data() {
    return {
      calendarCompleted: false,
      onboardingCompleted: false,
    };
  },
  computed: {
    user() {
      return this.currentUserStore.currentUser;
    },
    csrf() {
      return document.querySelectorAll('[name="csrf-token"]')[0].content;
    },
  },
  watch: {
    calendarCompleted(newValue) {
      if (newValue) {
        this.$emit('go-forward');
      }
    },
    onboardingCompleted(newValue) {
      if (newValue) {
        this.$emit('go-forward');
      }
    },
  },
  async mounted() {
    await this.getUser();
  },
  methods: {
    async getUser() {
      await this.currentUserStore.loadCurrentUser();
      this.onboardingCompleted = this.currentUserStore.currentUserAccountDetails.isOnboardingCompleted;

      if (!this.onboardingCompleted) {
        await this.userCalendarStore.loadCalendarAuthStatus(this.user);
        this.calendarCompleted =
          this.userCalendarStore.currentUserCalendar.isGoogleAuthenticated ||
          this.userCalendarStore.currentUserCalendar.isMicrosoftAuthenticated
            ? true
            : false;
      }
    },
    goBack() {
      this.$emit('go-back');
    },
    async getStarted() {
      await this.currentUserStore.updateUser({
        user: this.user,
        updatedUserAttributes: { onboarding_completed: true },
      });
      await this.$router.push('/');
    },
    async handleSkipConfirm() {
      await this.currentUserStore.updateUser({
        user: this.user,
        updatedUserAttributes: { onboarding_completed: true },
      });
      this.$emit('go-forward');
    },
  },
};
</script>
